import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const blank = () => ({ q: "", options: ["", "", ""], answer: 0 });

export function QuizEditorCard() {
  const [guides, setGuides] = useState([]);
  const [slug, setSlug] = useState("");
  const [qs, setQs] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/training/cleaner-required").then((r) => {
      setGuides(r.data.options);
      setSlug(r.data.slugs[0] || r.data.options[0]?.slug || "");
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (slug) api.get(`/training/quizzes/${slug}/edit`).then((r) => setQs(r.data.questions)).catch(() => setQs([]));
  }, [slug]);
  const setQ = (i, patch) => setQs(qs.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const setOpt = (i, k, v) => setQ(i, { options: qs[i].options.map((o, j) => (j === k ? v : o)) });
  const save = async () => {
    setBusy(true);
    try { await api.put(`/training/quizzes/${slug}`, { questions: qs }); toast.success(qs.length ? "Quiz saved" : "Quiz removed for this guide"); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="ns-card p-8 mt-6" data-testid="settings-quiz-editor">
      <div className="font-serif text-2xl">Guide quizzes</div>
      <p className="text-sm text-slate-500 mt-1 mb-4">Cleaners must answer every question correctly before a guide counts as read. Remove all questions to switch a guide's quiz off.</p>
      <select className="h-10 w-full max-w-md rounded-md border border-slate-200 bg-white px-3 text-sm mb-4" value={slug}
        onChange={(e) => setSlug(e.target.value)} data-testid="quiz-editor-guide">
        {guides.map((g) => <option key={g.slug} value={g.slug}>{g.title}</option>)}
      </select>
      <div className="space-y-4">
        {qs.map((q, i) => (
          <div key={i} className="rounded-xl border border-slate-100 p-4" data-testid={`quiz-editor-q-${i}`}>
            <div className="flex gap-2 items-center mb-2">
              <span className="text-xs text-slate-400 w-5">{i + 1}.</span>
              <Input value={q.q} placeholder="Question" onChange={(e) => setQ(i, { q: e.target.value })} data-testid={`quiz-editor-q-${i}-text`} />
              <button onClick={() => setQs(qs.filter((_, j) => j !== i))} className="text-slate-300 hover:text-red-600 transition-colors" data-testid={`quiz-editor-q-${i}-remove`}><Trash2 className="w-4 h-4" /></button>
            </div>
            {q.options.map((o, k) => (
              <div key={k} className="flex gap-2 items-center ml-7 mb-1.5">
                <input type="radio" name={`ans-${i}`} checked={q.answer === k} onChange={() => setQ(i, { answer: k })} title="Correct answer" data-testid={`quiz-editor-q-${i}-correct-${k}`} />
                <Input className="h-8 text-xs" value={o} placeholder={`Option ${k + 1}`} onChange={(e) => setOpt(i, k, e.target.value)} data-testid={`quiz-editor-q-${i}-opt-${k}`} />
                {q.options.length > 2 && (
                  <button onClick={() => setQ(i, { options: q.options.filter((_, j) => j !== k), answer: q.answer === k ? 0 : q.answer > k ? q.answer - 1 : q.answer })}
                    className="text-slate-300 hover:text-red-600 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
            ))}
            {q.options.length < 6 && (
              <button onClick={() => setQ(i, { options: [...q.options, ""] })} className="ml-7 text-[11px] text-teal-700 hover:text-teal-900" data-testid={`quiz-editor-q-${i}-add-opt`}>+ option</button>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-4">
        <Button variant="outline" className="rounded-full" disabled={qs.length >= 10} onClick={() => setQs([...qs, blank()])} data-testid="quiz-editor-add-question"><Plus className="w-4 h-4 mr-1" /> Add question</Button>
        <Button className="rounded-full ns-btn-primary" disabled={busy || !slug} onClick={save} data-testid="quiz-editor-save">{busy ? "Saving…" : "Save quiz"}</Button>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">Tick the circle next to the correct answer. Changes apply immediately.</p>
    </div>
  );
}

export function CheckoutPhotoSettingCard() {
  const [n, setN] = useState("");
  useEffect(() => { api.get("/settings/checkout-photos").then((r) => setN(r.data.required)).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put("/settings/checkout-photos", { required: Number(n) }); toast.success("Saved"); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div className="ns-card p-8 mt-6" data-testid="settings-checkout-photos">
      <div className="font-serif text-2xl">Photo check-out</div>
      <p className="text-sm text-slate-500 mt-1 mb-4">Cleaners must photograph every room on the property's checklist (Properties → edit → Photo check-out checklist) before marking a turnover Done. Photos are deleted after 90 days.</p>
      <div className="flex gap-3 items-center">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={Number(n) > 0} onChange={(e) => setN(e.target.checked ? 1 : 0)} data-testid="settings-checkout-photos-required" />
          Require room photos at check-out
        </label>
        <Button className="rounded-full ns-btn-primary" onClick={save} data-testid="settings-checkout-photos-save">Save</Button>
      </div>
    </div>
  );
}
